import React, { useState, useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { AppDispatch } from "../store";
import { loginSuccess, setAccessToken } from "../store/slices/authSlice";
import { API_BASE_URL, OAUTH_CONFIG } from "../config";
import TwitterLogin from "./TwitterLogin";
import Logo from "./Logo";
import "./LoginForm.css";
import {
  useUserLoginMutation,
  useUserRegisterMutation,
  useOauthGoogleLoginMutation,
} from "features/auth/api/authApi";
import type { OAuthGoogleRequest } from "features/auth/model/types";
import { isRequiresTwoFactor } from "features/auth/model/types";
import { TelegramVerifyModal } from "features/two-factor-verify";
import { ForgotPasswordModal } from "features/password-recovery";
import type { AuthResponse, RequiresTwoFactorResponse } from "entities/user";
import closeIcon from "assets/svg/close.svg";

import { useGoogleLogin } from "@react-oauth/google";

interface LoginFormProps {
  type: "register" | "login";
}

const LoginForm: React.FC<LoginFormProps> = ({ type }) => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [twoFactorChallenge, setTwoFactorChallenge] =
    useState<RequiresTwoFactorResponse | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [userLoginData, { isLoading: isLoadingLoginData }] =
    useUserLoginMutation();
  const [userRegisterData, { isLoading: isLoadingRegisterData }] =
    useUserRegisterMutation();
  const [oauthGoogleLogin, { isLoading: isLoadingOauthGoogleLogin }] =
    useOauthGoogleLoginMutation();

  const currentYear = new Date().getFullYear();

  const handleOAuthLogin = useCallback(
    async (provider: string, token: string, userInfo?: any) => {
      try {
        setLoading(provider);
        setError(null);

        const response = await fetch(`${API_BASE_URL}/auth/oauth/${provider}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            oauth_token: token,
            oauth_id: userInfo?.id,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          dispatch(
            loginSuccess({
              user: data.user,
              access_token: data.access_token,
            }),
          );
        } else {
          throw new Error(data.message || "Authorization error");
        }
      } catch (error) {
        setError(
          `Authorization error via ${provider}: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      } finally {
        setLoading(null);
      }
    },
    [dispatch],
  );
  //================= Редирект после возвращения от Google с кодом авторизации =================
  // useEffect(() => {
  //   // Проверяем, не вернулись ли мы от Google
  //   const urlParams = new URLSearchParams(window.location.search);
  //   const code = urlParams.get("code");
  //   const error = urlParams.get("error");

  //   if (error) {
  //     setError(`Google auth error: ${error}`);
  //     // Чистим URL от параметров
  //     window.history.replaceState({}, "", window.location.pathname);
  //     return;
  //   }

  //   if (code && !loading) {
  //     // Сохраняем state, если он был
  //     const savedState = sessionStorage.getItem("google_oauth_state");
  //     const currentState = urlParams.get("state");

  //     if (savedState && currentState && savedState !== currentState) {
  //       setError("State mismatch - possible CSRF attack");
  //       window.history.replaceState({}, "", window.location.pathname);
  //       return;
  //     }

  //     setLoading("google");

  //     fetch("https://твой-бэкенд.com/api/auth/google", {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({ code, state: currentState }),
  //     })
  //       .then((res) => res.json())
  //       .then((data) => {
  //         if (data.token) {
  //           localStorage.setItem("token", data.token);
  //           // Редирект в приложение или перезагрузка
  //           window.location.href = "/dashboard";
  //         } else {
  //           throw new Error("No token");
  //         }
  //       })
  //       .catch((err) => {
  //         setError(`Auth failed: ${err.message}`);
  //         setLoading(null);
  //       })
  //       .finally(() => {
  //         window.history.replaceState({}, "", window.location.pathname);
  //       });
  //   }
  // }, [loading]);
  //================================================================================================

  // const handleGoogleLogin = () => {
  //   if (loading) return;

  //   try {
  //     setLoading("google");
  //     setError(null);

  //     const state = Math.random().toString(36).substring(7);

  //     const redirectUri = OAUTH_CONFIG.google.redirectUri;

  //     const authUrl =
  //       `${OAUTH_CONFIG.google.authUrl}?` +
  //       `client_id=${OAUTH_CONFIG.google.clientId}&` +
  //       `response_type=code&` +
  //       `state=${state}&` +
  //       `redirect_uri=${encodeURIComponent(redirectUri)}&` +
  //       `scope=${encodeURIComponent(OAUTH_CONFIG.google.scope)}&` +
  //       `access_type=offline&` +
  //       `prompt=consent`;

  //     window.location.href = authUrl;
  //   } catch (error) {
  //     setError(
  //       `Initialization error for Google auth: ${error instanceof Error ? error.message : "Unknown error"}`,
  //     );
  //     setLoading(null);
  //   }
  // };

  const handleGoogleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      setLoading("google");

      try {
        const oauthRequest: OAuthGoogleRequest = {
          code: tokenResponse.code,
          redirect_uri: "postmessage",
        };

        const result = await oauthGoogleLogin(oauthRequest).unwrap();

        if (isRequiresTwoFactor(result)) {
          setTwoFactorChallenge(result);
          setLoading(null);
          return;
        }

        dispatch(setAccessToken(result.access_token));
        window.location.href = "/dashboard";
      } catch (err) {
        setError(
          `Auth failed: ${err instanceof Error ? err.message : "Unknown error"}`,
        );
        setLoading(null);
      }
    },
    onError: (error) => {
      setError(`Google auth error: ${error}`);
      setLoading(null);
    },
  });
  const handleRedditLogin = () => {
    if (loading) return;

    try {
      setLoading("reddit");
      setError(null);

      const state = Math.random().toString(36).substring(7);
      sessionStorage.setItem("reddit_oauth_state", state);

      const redirectUri = OAUTH_CONFIG.reddit.redirectUri;

      const authUrl =
        `https://www.reddit.com/api/v1/authorize?` +
        `client_id=${OAUTH_CONFIG.reddit.clientId}&` +
        `response_type=code&` +
        `state=${state}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `scope=${OAUTH_CONFIG.reddit.scope}&` +
        `duration=temporary`;

      window.location.href = authUrl;
    } catch (error) {
      setError(
        `Initialization error for Reddit auth: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setLoading(null);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    let valid = true;
    if (!email.trim()) {
      setEmailError("Please enter your email");
      valid = false;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      valid = false;
    } else {
      setEmailError("");
    }
    if (!password) {
      setPasswordError("Please enter your password");
      valid = false;
    } else if (password.length < 4) {
      setPasswordError("Password must be at least 4 characters");
      valid = false;
    } else if (password.length > 20) {
      setPasswordError("Password must be no more than 20 characters");
      valid = false;
    } else {
      setPasswordError("");
    }
    return valid;
  };

  // const handleEmailLogin = async (e: React.FormEvent) => {
  //   e.preventDefault();

  //   if (!validateEmail(email)) {
  //     setError("Please enter a valid email address");
  //     return;
  //   }

  //   try {
  //     setLoading("email");
  //     setError(null);

  //     const response = await fetch(`${API_BASE_URL}/auth/email/send-code`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({ email }),
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       setCodeSent(true);
  //     } else {
  //       throw new Error(data.message || "Error sending code");
  //     }
  //   } catch (error) {
  //     setError(
  //       `Error sending code: ${error instanceof Error ? error.message : "Unknown error"}`,
  //     );
  //   } finally {
  //     setLoading(null);
  //   }
  // };

  // const handleVerifyCode = async (e: React.FormEvent) => {
  //   e.preventDefault();

  //   if (!verificationCode.trim()) {
  //     setError("Please enter the verification code");
  //     return;
  //   }

  //   try {
  //     setLoading("verify");
  //     setError(null);

  //     const response = await fetch(`${API_BASE_URL}/auth/email/verify-code`, {
  //       method: "POST",
  //       headers: {
  //         "Content-Type": "application/json",
  //       },
  //       body: JSON.stringify({
  //         email,
  //         code: verificationCode,
  //       }),
  //     });

  //     const data = await response.json();

  //     if (response.ok) {
  //       dispatch(
  //         loginSuccess({
  //           user: data.user,
  //           access_token: data.access_token,
  //           refresh_token: data.refresh_token,
  //         }),
  //       );

  //       // redirect to landing page after successful email verification
  //       history.push("/dashboard");
  //     } else {
  //       throw new Error(data.message || "Invalid verification code");
  //     }
  //   } catch (error) {
  //     setError(
  //       `Verification error: ${error instanceof Error ? error.message : "Unknown error"}`,
  //     );

  //     const formCode = document.getElementById("code");
  //     const formText = document.getElementById("email-login-form-text");

  //     if (formCode instanceof HTMLElement && formText instanceof HTMLElement) {
  //       formCode.classList.add("email-login-form-error");
  //       formText.classList.add("email-login-form-error");
  //       formText.textContent = `Verification error: ${error instanceof Error ? error.message : "Unknown error"}`;
  //     }
  //   } finally {
  //     setLoading(null);
  //   }
  // };

  const handleEmailLoginWithoutCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setError(null);

    try {
      const res = await userLoginData({ email, password }).unwrap();

      if (isRequiresTwoFactor(res)) {
        setTwoFactorChallenge(res);
        return;
      }

      dispatch(
        loginSuccess({
          access_token: res.access_token,
          user: res.user,
        }),
      );
    } catch (err: any) {
      setError(
        `Login error: ${err?.data?.message || err?.error || "Unknown error"}`,
      );
    }
  };

  const handleRegisterWithoutCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setError(null);

    try {
      const res = await userRegisterData({ email, password }).unwrap();

      dispatch(
        loginSuccess({
          access_token: res.access_token,
          user: res.user,
        }),
      );
    } catch (err: any) {
      setError(
        `Register error: ${err?.data?.message || err?.error || "Unknown error"}`,
      );
    }
  };

  const handleTwoFactorVerified = (result: AuthResponse) => {
    dispatch(loginSuccess(result));
    setTwoFactorChallenge(null);
  };

  const twoFactorModal = twoFactorChallenge && (
    <TelegramVerifyModal
      challengeId={twoFactorChallenge.challenge_id}
      expiresAt={twoFactorChallenge.expires_at}
      message={twoFactorChallenge.message}
      onSuccess={handleTwoFactorVerified}
      onClose={() => setTwoFactorChallenge(null)}
    />
  );

  const forgotPasswordModal = showForgotPassword && (
    <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
  );

  if (showEmailForm) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="email-login-header">
            <button
              className="button email-login-back-button"
              onClick={() => {
                setShowEmailForm(false);
                setEmail("");
                setError(null);
              }}
            >
              <img src="assets/svg/arrow.svg" alt="Arrow back icon" />
              Back to login options
            </button>
            <h3 className="email-login-title">
              {type === "login" ? "Email Login" : "Email Register"}
            </h3>
          </div>
          <div className="email-login-body">
            <form
              onSubmit={
                type === "login"
                  ? handleEmailLoginWithoutCode
                  : handleRegisterWithoutCode
              }
              className="email-login-form"
            >
              <div className="email-login-form-fields">
                <div className="email-login-form-field">
                  <label htmlFor="email" className="email-login-form-label">
                    Email
                  </label>
                  <input
                    type="text"
                    className={`email-login-form-input${emailError ? " email-login-form-input--error" : ""}`}
                    id="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    placeholder="Enter your real email"
                  />
                  <div
                    className={`email-login-form-text${emailError ? " email-login-form-text--error" : ""}`}
                  >
                    {emailError}
                  </div>
                </div>
                <div className="email-login-form-field">
                  <label htmlFor="password" className="email-login-form-label">
                    Password
                  </label>
                  <input
                    type="password"
                    className={`email-login-form-input${passwordError ? " email-login-form-input--error" : ""}`}
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                      setError(null);
                    }}
                    placeholder="Enter your password"
                  />
                  <div
                    className={`email-login-form-text${passwordError || error ? " email-login-form-text--error" : ""}`}
                  >
                    {passwordError || error}
                  </div>
                  {type === "login" && (
                    <button
                      type="button"
                      className="email-login-forgot-password"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
              </div>

              <button
                className="email-login-form-button"
                type="submit"
                disabled={
                  type === "login" ? isLoadingLoginData : isLoadingRegisterData
                }
              >
                {isLoadingLoginData || isLoadingRegisterData
                  ? "Requesting..."
                  : type === "register"
                    ? "Register"
                    : "Log In"}
              </button>
            </form>
          </div>
        </div>
        {twoFactorModal}
        {forgotPasswordModal}
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-header-logo">
            <Logo />
            <button
              className="login-close-button"
              aria-label="Close"
              onClick={() => history.push("/")}
            >
              <img src={closeIcon} alt="Close icon" />
            </button>
          </div>
          <p className="login-header-text">Professional Trading Platform</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="login-buttons">
          <button
            className="login-form-button"
            onClick={handleGoogleLogin}
            disabled={loading !== null}
          >
            <img src="assets/svg/google.svg" alt="Google logo" />
            {loading === "google" ? "Connecting..." : "Continue with Google"}
          </button>

          <TwitterLogin
            className="login-form-button"
            buttonText="Continue with Twitter"
          />

          <button
            className="login-form-button"
            onClick={handleRedditLogin}
            disabled={loading !== null}
          >
            <img src="assets/svg/reddit.svg" alt="Reddit logo" />
            {loading === "reddit" ? "Connecting..." : "Continue with Reddit"}
          </button>

          <div className="divider">
            <span>or</span>
          </div>

          <button
            className="login-form-button email"
            onClick={() => setShowEmailForm(true)}
            disabled={!!loading}
            style={{ width: "100%" }}
          >
            <img src="assets/svg/email.svg" alt="Email logo" />
            {type === "login" ? "Login with Email" : "Register with Email"}
          </button>
        </div>

        <div className="login-footer">
          <p>&copy; {currentYear} ProfitOption. All rights reserved.</p>
        </div>
      </div>
      {twoFactorModal}
    </div>
  );
};

declare global {
  interface Window {
    google: any;
    FB: any;
    fbAsyncInit: () => void;
  }
}

export default LoginForm;
